import React from "react";
import Layout from "../components/Layout/Layout";
import { useNavigate } from "react-router-dom";
import {
  FiShield,
  FiDatabase,
  FiShare2,
  FiLock,
  FiClock,
  FiMail,
  FiCheckCircle,
} from "react-icons/fi";

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  const PALETTE = {
    DARK_NAVY: "#1C2E4A",
    ACCENT_BROWN: "#7C4F2B",
    LIGHT_CREAM: "#FAF8F5",
  };

  return (
    <Layout title={"Russh Berry | Privacy Policy & Data Security"}>
      <div
        className="min-h-screen pt-24 pb-20 transition-colors duration-500"
        style={{ backgroundColor: PALETTE.LIGHT_CREAM }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {/* HEADER SECTION */}
          <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
            <p
              className="text-xs sm:text-sm font-semibold tracking-[0.3em] uppercase mb-3"
              style={{ color: PALETTE.ACCENT_BROWN }}
            >
              Russh Berry Policies
            </p>
            <h1
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 leading-tight"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Privacy Policy
            </h1>
            <p className="text-xs font-semibold tracking-widest uppercase text-gray-400 mb-6">
              Effective Date: 12 July 2026
            </p>
            <div
              className="w-16 h-[1px] mx-auto mb-6"
              style={{ backgroundColor: PALETTE.ACCENT_BROWN }}
            />
            <p className="text-gray-600 text-sm sm:text-base font-light leading-relaxed">
              Russh Berry is committed to protecting your privacy and ensuring
              the security of your personal information. This Privacy Policy
              explains how we collect, use, disclose, and safeguard your
              information when you visit our website, place an order, or
              otherwise interact with us.
            </p>
          </div>

          {/* 2-COLUMN GRID ON DESKTOP TO FILL HORIZONTAL SPACE */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10 items-stretch mb-12">
            {/* 1. INFORMATION COLLECTION */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiDatabase size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Information We Collect
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    We may collect personal information including your name,
                    email address, phone number, billing and shipping addresses,
                    payment details, and order history. We may also collect
                    technical information such as your IP address, browser type,
                    device information, and browsing behavior on our website to
                    improve our services and enhance your shopping experience.
                  </p>
                </div>
              </div>
            </div>

            {/* 2. HOW WE USE YOUR INFORMATION */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiCheckCircle size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    How We Use Your Information
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    Your information is used to process and fulfill orders,
                    provide customer support, communicate with you regarding
                    purchases, send transactional notifications, and, where you
                    have consented, share promotional offers and updates from
                    Russh Berry. You may opt out of marketing communications at
                    any time by using the unsubscribe link provided in our
                    emails or by contacting us directly.
                  </p>
                </div>
              </div>
            </div>

            {/* 3. INFORMATION SHARING & PARTNERS (HIGHLIGHTED BLOCK) */}
            <div
              className="bg-[#FAF8F5] p-8 sm:p-10 lg:p-12 shadow-md border-2 transition-all duration-300 hover:shadow-xl relative overflow-hidden flex flex-col justify-between"
              style={{
                borderColor: PALETTE.ACCENT_BROWN,
                borderLeft: `8px solid ${PALETTE.ACCENT_BROWN}`,
              }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-white flex items-center justify-center shrink-0 mt-1 shadow-sm"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiShare2 size={22} />
                </div>
                <div>
                  <span
                    className="inline-block text-[11px] font-semibold tracking-[0.2em] uppercase px-3 py-1 rounded-full bg-white mb-3 shadow-2xs"
                    style={{ color: PALETTE.ACCENT_BROWN }}
                  >
                    Trusted Partners
                  </span>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Information Sharing
                  </h2>
                  <p className="text-gray-800 font-normal text-sm sm:text-base leading-relaxed">
                    We do not sell your personal information. We may share it
                    only with trusted service providers as necessary to operate
                    our business, including{" "}
                    <span className="font-semibold">Razorpay</span> for payment
                    processing and{" "}
                    <span className="font-semibold">Delhivery</span> and{" "}
                    <span className="font-semibold">Shiprocket</span> for
                    shipping and logistics services.
                  </p>
                </div>
              </div>
            </div>

            {/* 4. DATA SECURITY & PROTECTION */}
            <div
              className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl flex flex-col justify-between"
              style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
            >
              <div className="flex items-start gap-6">
                <div
                  className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                  style={{ color: PALETTE.ACCENT_BROWN }}
                >
                  <FiLock size={22} />
                </div>
                <div>
                  <h2
                    className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                    style={{ color: PALETTE.DARK_NAVY }}
                  >
                    Data Security
                  </h2>
                  <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed">
                    We implement reasonable administrative, technical, and
                    physical safeguards to protect your data. However, no method
                    of transmission over the internet or electronic storage is
                    completely secure, and therefore absolute security cannot be
                    guaranteed.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* 5. DATA RETENTION & USER RIGHTS (FULL WIDTH CARD) */}
          <div
            className="bg-white p-8 sm:p-10 lg:p-12 shadow-md border border-gray-100/80 transition-all duration-300 hover:shadow-xl mb-12"
            style={{ borderLeft: `4px solid ${PALETTE.DARK_NAVY}` }}
          >
            <div className="flex items-start gap-6">
              <div
                className="w-12 h-12 rounded-full bg-[#FAF8F5] flex items-center justify-center shrink-0 mt-1"
                style={{ color: PALETTE.ACCENT_BROWN }}
              >
                <FiClock size={22} />
              </div>
              <div className="max-w-4xl">
                <h2
                  className="text-xl sm:text-2xl font-light tracking-wider uppercase mb-4"
                  style={{ color: PALETTE.DARK_NAVY }}
                >
                  Data Retention &amp; Your Rights
                </h2>
                <p className="text-gray-700 font-light text-sm sm:text-base leading-relaxed mb-4">
                  Personal information is retained for up to 11 months from the
                  date of collection or as otherwise required by applicable law.
                  You may request access to, correction of, or deletion of your
                  personal information by contacting us directly at{" "}
                  <a
                    href="mailto:support@russhberry.com"
                    className="font-medium underline decoration-[#7C4F2B] hover:text-[#7C4F2B] transition-colors"
                  >
                    support@russhberry.com
                  </a>
                  .
                </p>
                <p className="text-gray-500 font-light text-xs sm:text-sm leading-relaxed border-t border-gray-100 pt-4 mt-4">
                  <span className="font-medium">
                    Governing Law &amp; Updates:
                  </span>{" "}
                  This Privacy Policy is governed by and construed in accordance
                  with the laws of India. We may update this Policy from time to
                  time, and any changes will be posted on this page with the
                  revised effective date. By using our website, you acknowledge
                  and agree to the terms of this Privacy Policy.
                </p>
              </div>
            </div>
          </div>

          {/* FOOTER ASSISTANCE SECTION */}
          <div className="mt-16 sm:mt-20 text-center bg-white p-8 sm:p-12 border border-gray-200/60 shadow-sm w-full">
            <div
              className="w-10 h-10 rounded-full bg-[#FAF8F5] flex items-center justify-center mx-auto mb-4"
              style={{ color: PALETTE.ACCENT_BROWN }}
            >
              <FiShield size={20} />
            </div>
            <h3
              className="text-lg sm:text-xl font-light tracking-widest uppercase mb-2"
              style={{ color: PALETTE.DARK_NAVY }}
            >
              Russh Berry Clothing Brand
            </h3>
            <p className="text-gray-500 font-light text-sm mb-6 max-w-md mx-auto">
              If you have any questions, concerns, or privacy requests regarding
              your personal information, please get in touch with us.
            </p>
            <button
              onClick={() => navigate("/contact")}
              className="inline-flex items-center gap-3 px-8 py-3.5 text-xs font-semibold tracking-[0.25em] uppercase text-white transition-all duration-300 shadow-md hover:shadow-xl"
              style={{ backgroundColor: PALETTE.DARK_NAVY }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.ACCENT_BROWN;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = PALETTE.DARK_NAVY;
              }}
            >
              <FiMail className="text-sm" />
              <span>Contact Privacy Team</span>
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
